import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Lock } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { OfficerHandover, HandoverHistory } from "./OfficerHandover";

function PermToggle({ perm, checked, onChange, disabled, roleName }) {
  return (
    <label className={`flex cursor-pointer items-start gap-2 rounded-lg border p-2 text-xs transition-colors ${checked ? "border-crimson/40 bg-gold-light/60" : "border-gold/20 bg-white/40"} ${disabled ? "opacity-60" : ""}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-0.5" data-testid={`perm-${roleName}-${perm.key}`} />
      <span><span className="font-semibold">{perm.label.split(" — ")[0]}</span><br /><span className="text-muted-foreground">{perm.label.split(" — ")[1]}</span></span>
    </label>
  );
}

function RoleTitle({ role }) {
  return (
    <div className="flex items-center gap-2">
      {role.builtin ? <Lock size={14} className="text-gold" /> : <ShieldCheck size={14} className="text-palm" />}
      <h4 className="font-serif-display text-lg font-bold text-crimson">{role.name}</h4>
      <span className="rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-crimson" data-testid={`role-members-${role.name}`}>{role.members} member{role.members === 1 ? "" : "s"}</span>
      {!role.builtin && <span className="rounded-full bg-palm/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-palm">Custom</span>}
    </div>
  );
}

function RoleCard({ role, perms, canManage, onChanged }) {
  const [selected, setSelected] = useState(role.permissions || []);
  const dirty = JSON.stringify([...selected].sort()) !== JSON.stringify([...(role.permissions || [])].sort());
  const save = async () => {
    try { await api.put(`/admin/roles/${encodeURIComponent(role.name)}`, { permissions: selected }); toast.success(`${role.name} permissions saved`); onChanged(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete role "${role.name}"? ${role.members} member(s) will become plain members.`)) return;
    try { await api.delete(`/admin/roles/${encodeURIComponent(role.name)}`); toast.success("Role deleted"); onChanged(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="glass rounded-2xl p-4" data-testid={`role-card-${role.name}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <RoleTitle role={role} />
        <div className="flex gap-2">
          {canManage && dirty && <Button size="sm" className="bg-crimson" onClick={save} data-testid={`role-save-${role.name}`}>Save changes</Button>}
          {canManage && !role.builtin && <Button size="sm" variant="ghost" className="text-destructive" onClick={remove} data-testid={`role-delete-${role.name}`}><Trash2 size={14} /></Button>}
        </div>
      </div>
      {role.description && <p className="mb-2 text-xs text-muted-foreground">{role.description}</p>}
      <div className="grid gap-2 sm:grid-cols-3">
        {perms.map((p) => (
          <PermToggle key={p.key} perm={p} roleName={role.name} checked={selected.includes(p.key)} disabled={!canManage}
            onChange={(v) => setSelected(v ? [...selected, p.key] : selected.filter((x) => x !== p.key))} />
        ))}
      </div>
    </div>
  );
}

function NewRoleDialog({ perms, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", permissions: [] });
  const create = async () => {
    try { await api.post("/admin/roles", form); toast.success(`Role "${form.name}" created`); setOpen(false); setForm({ name: "", description: "", permissions: [] }); onCreated(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-crimson" data-testid="new-role-btn"><Plus size={16} className="mr-1" /> New role</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Create a custom role</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Role name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Youth Coordinator" data-testid="new-role-name" /></div>
          <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-role-desc" /></div>
          <div className="grid gap-2 sm:grid-cols-2">
            {perms.map((p) => (
              <PermToggle key={p.key} perm={p} roleName="new" checked={form.permissions.includes(p.key)}
                onChange={(v) => setForm({ ...form, permissions: v ? [...form.permissions, p.key] : form.permissions.filter((x) => x !== p.key) })} />
            ))}
          </div>
        </div>
        <DialogFooter><Button onClick={create} disabled={!form.name.trim()} className="bg-crimson" data-testid="new-role-save">Create role</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RolesAccess({ canManage }) {
  const { data: roles, reload } = useApi("/admin/roles");
  const { data: perms } = useApi("/admin/permissions");
  const [histKey, setHistKey] = useState(0);
  const list = roles || [];
  const custom = list.filter((r) => !r.builtin);
  const builtin = list.filter((r) => r.builtin);
  return (
    <div className="space-y-6" data-testid="roles-access">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Every officer role carries a set of permissions enforced by the server. Members with the plain <span className="font-mono-accent">member</span> role have portal access only.</p>
        {canManage && <div className="flex gap-2"><OfficerHandover roles={list.map((r) => r.name)} onDone={() => { reload(); setHistKey((k) => k + 1); }} /><NewRoleDialog perms={perms || []} onCreated={reload} /></div>}
      </div>
      <HandoverHistory key={histKey} />
      {custom.length > 0 && (
        <section><h3 className="eyebrow mb-2 text-gold">Custom roles</h3>
          <div className="grid gap-4 lg:grid-cols-2">{custom.map((r) => <RoleCard key={r.name} role={r} perms={perms || []} canManage={canManage} onChanged={reload} />)}</div></section>
      )}
      <section><h3 className="eyebrow mb-2 text-gold">Built-in roles</h3>
        <div className="grid gap-4 lg:grid-cols-2">{builtin.map((r) => <RoleCard key={r.name} role={r} perms={perms || []} canManage={canManage} onChanged={reload} />)}</div></section>
    </div>
  );
}
